import React from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTrackEvent } from '@jotpsych/statsig';

import type { ProviderRuleResponse } from '../types';

interface DeleteRuleDialogProps {
  open: boolean;
  rule: ProviderRuleResponse | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export const DeleteRuleDialog: React.FC<DeleteRuleDialogProps> = ({
  open,
  rule,
  onClose,
  onConfirm,
  isDeleting = false,
}) => {
  const { trackEvent } = useTrackEvent();

  const handleConfirm = () => {
    if (rule) {
      trackEvent('rule_deleted', {
        metadata: {
          rule_id: rule.rule_id,
          rule_type: rule.rule_type,
          is_global: String(rule.is_global),
        },
      });
    }
    onConfirm();
  };

  if (!rule) return null;

  const ruleTitle =
    rule.rule_type === 'compliance'
      ? (rule.rule as any).title || (rule.rule as any).selector_text || 'Untitled Rule'
      : (rule.rule as any).title || 'Untitled Rule';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Delete Rule</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="body1" component="span" sx={{ display: 'block', mb: 1 }}>
            Are you sure you want to delete the rule &quot;{ruleTitle}&quot;?
          </Typography>
          <Typography variant="body2" color="error" component="span">
            This action cannot be undone.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
